import React, { useState, useEffect, useRef } from "react";
import {
  BarChart,
  CartesianGrid,
  Bar,
  XAxis,
  YAxis,
  Label,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { startOfHour, parseISO, format, isValid } from "date-fns";

import { i18n } from "../../translate/i18n";

// Helper function to safely format ticket time
const safeFormatTime = (dateValue) => {
  if (!dateValue) return null;
  try {
    let date;
    if (dateValue instanceof Date) {
      date = dateValue;
    } else if (typeof dateValue === "string") {
      date = parseISO(dateValue);
    } else if (typeof dateValue === "number") {
      date = new Date(dateValue);
    } else {
      return null;
    }
    if (!isValid(date)) return null;
    return format(startOfHour(date), "HH:mm");
  } catch {
    return null;
  }
};
import useTickets from "../../hooks/useTickets";
import { useThemeContext } from "../../context/DarkMode";

const Chart = () => {
  const { darkMode } = useThemeContext();

  const date = useRef(new Date().toISOString());
  const { tickets } = useTickets({ date: date.current });

  const [chartData, setChartData] = useState([
    { time: "08:00", amount: 0 },
    { time: "09:00", amount: 0 },
    { time: "10:00", amount: 0 },
    { time: "11:00", amount: 0 },
    { time: "12:00", amount: 0 },
    { time: "13:00", amount: 0 },
    { time: "14:00", amount: 0 },
    { time: "15:00", amount: 0 },
    { time: "16:00", amount: 0 },
    { time: "17:00", amount: 0 },
    { time: "18:00", amount: 0 },
    { time: "19:00", amount: 0 },
  ]);

  useEffect(() => {
    setChartData((prevState) => {
      let aux = [...prevState];

      aux.forEach((a) => {
        tickets.forEach((ticket) => {
          const ticketTime = safeFormatTime(ticket.createdAt);
          if (ticketTime === a.time) {
            a.amount++;
          }
        });
      });

      return aux;
    });
  }, [tickets]);

  // Colors based on theme
  const textColor = darkMode ? "#9ca3af" : "#6b7280";
  const gridColor = darkMode ? "#374151" : "#e5e7eb";
  const barColor = "#6366f1"; // primary-500

  return (
    <div>
      <h3 className="text-lg font-semibold text-primary-600 dark:text-primary-400 mb-4">
        {`${i18n.t("dashboard.charts.perDay.title")}${tickets.length}`}
      </h3>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            barSize={40}
            margin={{
              top: 8,
              right: 16,
              bottom: 20,
              left: 24,
            }}
          >
          <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
          <XAxis
            dataKey="time"
            stroke={textColor}
            tick={{ fill: textColor, fontSize: 12 }}
            tickLine={{ stroke: textColor }}
          />
          <YAxis
            type="number"
            allowDecimals={false}
            stroke={textColor}
            tick={{ fill: textColor, fontSize: 12 }}
            tickLine={{ stroke: textColor }}
          >
            <Label
              angle={270}
              position="left"
              style={{ textAnchor: "middle", fill: textColor, fontSize: 12 }}
            >
              Tickets
            </Label>
          </YAxis>
          <Tooltip
            contentStyle={{
              backgroundColor: darkMode ? "#1f2937" : "#ffffff",
              border: `1px solid ${gridColor}`,
              borderRadius: "8px",
              color: darkMode ? "#f3f4f6" : "#111827",
            }}
            labelStyle={{ color: darkMode ? "#9ca3af" : "#6b7280" }}
          />
          <Bar dataKey="amount" fill={barColor} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default Chart;
